import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getAllUsers } from "../../../../../api/addEmployee/employee";
import {
  useSendChatRequest,
  useCreateGroupChat,
  useChats,
} from "../../../../../api/Chats/chats";
import { jwtDecode } from "jwt-decode";
import {
  notifySuccess,
  notifyError,
} from "../../../../../utils/ToastifyProvider";

export default function NewConnectionModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  
  // ✅ FIX: Call the hooks at the component level
  const { mutate: sendChatRequest, isPending: isSendingRequest } = useSendChatRequest();
  const { mutate: createGroupChat, isPending: isCreatingGroup } = useCreateGroupChat();

  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [mode, setMode] = useState("single");
  const [role, setRole] = useState(null);
  const [currentUserId, setCurrentUserId] = useState("");

  const { data: allUsers = [], isFetching } = useQuery({
    queryKey: ["users"],
    queryFn: () => getAllUsers(""),
    enabled: isOpen,
  });

  // Fetch all chats to check existing conversations
  const token = localStorage.getItem("token");
  const { data: chatsData } = useChats({
    token,
    limit: 1000,
    search: "",
  });

  const allChats = chatsData?.pages?.flatMap((page) => page.chats) ?? [];

  // ✅ Filter out inactive users and current user
  const activeUsers = allUsers.filter((user) => {
    // Filter out current user
    if (user._id === currentUserId) return false;

    // Filter out inactive users
    if (user.isActive === false) return false;

    return true;
  });

  const filteredUsers = activeUsers.filter((user) => {
    if (!search) return true;
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const email = user.email.toLowerCase();
    const username = user.username?.toLowerCase() || "";
    const searchLower = search.toLowerCase();

    return (
      fullName.includes(searchLower) ||
      email.includes(searchLower) ||
      username.includes(searchLower)
    );
  });

  // Check if a chat already exists with a user
  const findExistingChat = (userId) => {
    return allChats.find((chat) => {
      if (chat.isGroupChat) return false;
      return chat.users?.some((user) => user._id === userId);
    });
  };

  // Reset search when mode changes
  useEffect(() => {
    setSearch("");
    setSelectedUser(null);
    setSelectedUsers([]);
  }, [mode]);

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setRole(decoded.role);
        setCurrentUserId(decoded.userid || decoded._id || decoded.id);
      } catch (error) {
        // console.error("Invalid token", error);
      }
    }
  }, [token]);

  if (!isOpen) return null;

  // ✅ FIX: Use the mutation correctly
  const handleSendRequest = () => {
    if (!selectedUser) return;

    // Check if chat already exists
    const existingChat = findExistingChat(selectedUser._id);

    if (existingChat) {
      // Open existing chat
      navigate(`/chats?chatId=${existingChat._id}`);
      resetAndClose();
      return;
    }

    // Send new request if chat doesn't exist
    sendChatRequest(selectedUser._id, {
      onSuccess: () => {
        resetAndClose();
      },
      onError: (error) => {
        notifyError(error?.response?.data?.message || "Failed to send request. Please try again.");
      },
    });
  };

  // ✅ FIX: Use the mutation correctly
  const handleCreateGroup = () => {
    if (!groupName.trim() || selectedUsers.length < 2) {
      notifyError("Please enter a group name and select at least 2 users.");
      return;
    }

    createGroupChat(
      { name: groupName, users: selectedUsers },
      {
        onSuccess: () => {
          resetAndClose();
        },
        onError: (error) => {
          notifyError(error?.response?.data?.message || "Failed to create group. Please try again.");
        },
      }
    );
  };

  const toggleUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const resetAndClose = () => {
    setSelectedUser(null);
    setSelectedUsers([]);
    setGroupName("");
    setSearch("");
    setMode("single");
    onClose();
  };

  const getSelectedUserNames = () => {
    return activeUsers
      .filter((user) => selectedUsers.includes(user._id))
      .map((user) => user.firstName)
      .join(", ");
  };

  const getButtonText = () => {
    if (!selectedUser) return "";

    const existingChat = findExistingChat(selectedUser._id);

    if (existingChat) {
      return `💬 Open Chat with ${selectedUser.firstName}`;
    }

    return role === "admin"
      ? `💬 Start Chat with ${selectedUser.firstName}`
      : `💬 Send Request to ${selectedUser.firstName}`;
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white w-[480px] max-w-[90vw] max-h-[90vh] rounded-2xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-100 to-blue-200 px-6 py-4 text-white relative">
          <button
            onClick={resetAndClose}
            className="absolute top-4 right-4 text-black/80 hover:text-black text-xl font-light"
          >
            ×
          </button>

          <h2 className="text-xl text-black font-semibold mb-3">
            {mode === "single" ? "New Chat" : "New Group"}
          </h2>

          {/* Mode Switch */}
          <div className="flex bg-white/20 rounded-lg p-1">
            <button
              onClick={() => setMode("single")}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                mode === "single"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-black/90 hover:text-black"
              }`}
            >
              💬 1-to-1 Chat
            </button>
            {role === "admin" && (
              <button
                onClick={() => setMode("group")}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  mode === "group"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-black/90 hover:text-black"
                }`}
              >
                👥 Group Chat
              </button>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search contacts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full placeholder:text-xs pl-10 pr-4 py-2 border-0 rounded-xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 text-gray-900 placeholder-gray-500"
            />
          </div>
        </div>

        {/* Selected Users Display for Group Mode */}
        {mode === "group" && selectedUsers.length > 0 && (
          <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-blue-600 font-medium">
                Selected ({selectedUsers.length}):
              </span>
              <span className="text-blue-800 truncate">
                {getSelectedUserNames()}
              </span>
            </div>
          </div>
        )}

        {/* User List */}
        <div className="flex-1 overflow-y-auto">
          {isFetching ? (
            <div className="flex items-center justify-center h-32">
              <div className="flex items-center gap-3 text-gray-500">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
                <span>Loading contacts...</span>
              </div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-500">
              <svg
                className="h-12 w-12 mb-2 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
                />
              </svg>
              <p className="text-sm">
                {search ? "No contacts found" : "No active contacts available"}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredUsers.map((user) => {
                const existingChat = findExistingChat(user._id);
                const hasExistingChat = !!existingChat;

                return (
                  <div
                    key={user._id}
                    onClick={() =>
                      mode === "single"
                        ? setSelectedUser(user)
                        : toggleUser(user._id)
                    }
                    className={`px-4 py-4 cursor-pointer flex items-center gap-4 transition-all hover:bg-gray-50 ${
                      mode === "single"
                        ? selectedUser?._id === user._id
                          ? "bg-blue-50 border-r-4 border-blue-500"
                          : ""
                        : selectedUsers.includes(user._id)
                        ? "bg-blue-50 border-r-4 border-blue-500"
                        : ""
                    }`}
                  >
                    {/* Profile Picture */}
                    <div className="relative">
                      <img
                        src={
                          user.profilePic?.url ||
                          `https://ui-avatars.com/api/?name=${user.firstName}+${user.lastName}&background=90D5FF&color=fff&size=48`
                        }
                        alt={user.username}
                        className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                      />
                      {/* Selection indicator for group mode */}
                      {mode === "group" && selectedUsers.includes(user._id) && (
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <svg
                            className="w-3 h-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {user.firstName} {user.lastName}
                      </p>
                      {mode === "single" && hasExistingChat && (
                        <p className="text-xs text-green-600 flex items-center gap-1 mt-0.5">
                          <svg
                            className="w-3 h-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Already connected
                        </p>
                      )}
                    </div>

                    {/* Selection indicator */}
                    {mode === "single" && selectedUser?._id === user._id && (
                      <div className="text-blue-500">
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-4 py-4 border-t border-gray-200 bg-gray-50">
          {mode === "single" && selectedUser && (
            <button
              onClick={handleSendRequest}
              disabled={isSendingRequest}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 px-4 rounded-xl font-medium disabled:from-green-300 disabled:to-green-400 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {isSendingRequest ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>
                    {findExistingChat(selectedUser._id)
                      ? "Opening Chat..."
                      : role === "admin"
                      ? "Starting Chat..."
                      : "Sending Request..."}
                  </span>
                </div>
              ) : (
                getButtonText()
              )}
            </button>
          )}

          {mode === "group" && (
            <div className="space-y-3">
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Enter group name (e.g., Project Team)"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-transparent"
              />
              <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                <span>
                  {selectedUsers.length} member
                  {selectedUsers.length !== 1 ? "s" : ""} selected
                </span>
                <span>Minimum 2 members required</span>
              </div>
              <button
                onClick={handleCreateGroup}
                disabled={
                  isCreatingGroup || !groupName.trim() || selectedUsers.length < 2
                }
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 px-4 rounded-xl font-medium disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                {isCreatingGroup ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>Creating Group...</span>
                  </div>
                ) : (
                  `👥 Create Group${
                    selectedUsers.length > 0
                      ? ` (${selectedUsers.length} members)`
                      : ""
                  }`
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}